/**
 * OpenAI BYOK credentials API (B1.3/B1.4).
 * Reads return metadata only — never apiKey / ciphertext.
 */
import { get, put, del, post } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type LlmProvider = 'openai';

export interface LlmCredentialMetadata {
  id?: string;
  tenantId?: string;
  agentId: string | null;
  provider: string;
  scope: 'tenant' | 'agent';
  configured: boolean;
  displayHint: string | null;
  status: string | null;
  validationStatus: string | null;
  lastValidatedAt: string | null;
  lastErrorCode: string | null;
  organizationId: string | null;
  keyNames: string[];
  updatedAt?: string;
  createdAt?: string;
}

export interface UpsertLlmCredentialPayload {
  provider?: LlmProvider;
  apiKey: string;
  organizationId?: string;
}

export interface LlmCredentialValidateResult {
  ok: boolean;
  metadata: LlmCredentialMetadata;
  errorCode?: string | null;
  message?: string;
}

export const llmCredentialsService = {
  list(): Promise<LlmCredentialMetadata[]> {
    return get<LlmCredentialMetadata[]>(endpoints.llmCredentials.list);
  },

  upsertTenant(
    payload: UpsertLlmCredentialPayload,
  ): Promise<LlmCredentialMetadata> {
    return put<LlmCredentialMetadata>(
      endpoints.llmCredentials.tenant,
      payload,
    );
  },

  removeTenant(provider: LlmProvider = 'openai'): Promise<void> {
    return del(
      `${endpoints.llmCredentials.tenant}?provider=${encodeURIComponent(provider)}`,
    );
  },

  validateTenant(
    provider: LlmProvider = 'openai',
  ): Promise<LlmCredentialValidateResult> {
    return post<LlmCredentialValidateResult>(
      `${endpoints.llmCredentials.validateTenant}?provider=${encodeURIComponent(provider)}`,
    );
  },

  getAgent(
    agentId: string,
    provider: LlmProvider = 'openai',
  ): Promise<LlmCredentialMetadata> {
    return get<LlmCredentialMetadata>(
      `${endpoints.llmCredentials.agent(agentId)}?provider=${encodeURIComponent(provider)}`,
    );
  },

  upsertAgent(
    agentId: string,
    payload: UpsertLlmCredentialPayload,
  ): Promise<LlmCredentialMetadata> {
    return put<LlmCredentialMetadata>(
      endpoints.llmCredentials.agent(agentId),
      payload,
    );
  },

  removeAgent(
    agentId: string,
    provider: LlmProvider = 'openai',
  ): Promise<void> {
    return del(
      `${endpoints.llmCredentials.agent(agentId)}?provider=${encodeURIComponent(provider)}`,
    );
  },

  validateAgent(
    agentId: string,
    provider: LlmProvider = 'openai',
  ): Promise<LlmCredentialValidateResult> {
    return post<LlmCredentialValidateResult>(
      `${endpoints.llmCredentials.validateAgent(agentId)}?provider=${encodeURIComponent(provider)}`,
    );
  },
} as const;

export function findTenantLlmCredential(
  rows: LlmCredentialMetadata[],
): LlmCredentialMetadata | null {
  return rows.find((r) => r.scope === 'tenant' && r.configured) ?? null;
}
